import { Edit3, PercentCircle, Trash2 } from 'lucide-react';

import { useSaleDispatcher } from '../../../../hooks/sale/useSaleDispatcher';

import { usePOSSelectedProduct } from '../../../../hooks/sale/usePOSSelectedProduct';
import { useSale } from '../../../../hooks/sale/useSale';

interface Props {
  product: any;
}

const POSSelectedProductCard: React.FC<Props> = ({ product }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const { deleteProduct, getProductById } = useSaleDispatcher();

  const { isCashierProcessingMode } = useSale();

  const { quantityLabel, formattedTotal } = usePOSSelectedProduct(product);

  const hasDiscount = Number(product.discountPercentage || 0) > 0;

  const appliesDiscount = product.appliesDiscount !== false;

  // =====================================================
  // 🔹 Actions
  // =====================================================
  const handleEdit = () => {
    getProductById(product.saleDetailId);
  };

  const handleRemove = () => {
    deleteProduct(product.saleDetailId);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className="
      border-b
      border-stroke

      py-4

      dark:border-strokedark
    "
    >
      {/* ===================================================== */}
      {/* 🔹 Header */}
      {/* ===================================================== */}
      <div
        className="
        flex
        items-start
        justify-between
        gap-3
      "
      >
        {/* 🔹 Product */}
        <div className="min-w-0 flex-1">
          <h4
            className="
            line-clamp-2

            text-sm
            font-medium
            leading-5

            text-black

            dark:text-white
          "
          >
            {product.productName}
          </h4>

          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p
              className="
              text-xs
              text-body
            "
            >
              {quantityLabel}
            </p>

            {hasDiscount && (
              <span
                className="
                rounded-md

                bg-danger/10

                px-2
                py-0.5

                text-[11px]
                font-semibold

                text-danger
              "
              >
                -{product.discountPercentage}%
              </span>
            )}

            {!appliesDiscount && (
              <span
                title="Este producto no admite descuentos"
                className="
                flex
                items-center
                gap-1

                rounded-md

                bg-gray-200

                px-2
                py-0.5

                text-[11px]
                font-semibold

                text-body

                dark:bg-meta-4
                dark:text-bodydark2
              "
              >
                <PercentCircle size={11} className="shrink-0" />
                No aplica descuento
              </span>
            )}
          </div>
        </div>

        {/* 🔹 Actions */}
        <div className="flex items-center gap-1">
          {/* 🔹 Edit */}
          <button
            onClick={handleEdit}
            disabled={isCashierProcessingMode}
            className={`
            rounded-lg

            p-1.5

            text-body

            transition-all
            duration-200

            ${
              isCashierProcessingMode
                ? `
                  cursor-not-allowed
                  opacity-40
                `
                : `
                  hover:bg-primary/10
                  hover:text-primary
                `
            }
          `}
          >
            <Edit3 size={16} />
          </button>

          {/* 🔹 Remove */}
          <button
            onClick={handleRemove}
            disabled={isCashierProcessingMode}
            className={`
            rounded-lg

            p-1.5

            text-body

            transition-all
            duration-200

            ${
              isCashierProcessingMode
                ? `
                  cursor-not-allowed
                  opacity-40
                `
                : `
                  hover:bg-danger/10
                  hover:text-danger
                `
            }
          `}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Footer */}
      {/* ===================================================== */}
      <div
        className="
        mt-4

        flex
        items-center
        justify-between
      "
      >
        {/* 🔹 Quantity */}
        <div
          className="
          rounded-lg

          border
          border-stroke

          bg-whiten

          px-3
          py-1.5

          text-sm
          font-medium

          text-black

          dark:border-strokedark
          dark:bg-meta-4
          dark:text-white
        "
        >
          {product.quantity} unidad(es)
        </div>

        {/* 🔹 Total */}
        <div className="text-right">
          <p
            className="
            text-xs
            text-body
          "
          >
            Total
          </p>

          <p
            className="
            text-sm
            font-semibold

            text-primary
          "
          >
            {formattedTotal}
          </p>
        </div>
      </div>
    </div>
  );
};

export default POSSelectedProductCard;
